import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { CheckCircle, Clock, Package, ExternalLink } from "lucide-react";

// Admin section – Kit-Order-Verwaltung + manuelle Zahlungsbestätigung
export default function RescueKitsAdminSection() {
  const [orders, setOrders] = useState([]);
  const [bags, setBags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("PENDING");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [o, b] = await Promise.all([
        api.get("/rescue/admin/kit/orders?limit=200"),
        api.get(`/rescue/admin/bags?limit=200${statusFilter ? `&status=${statusFilter}` : ""}`),
      ]);
      setOrders(o.data.orders || []);
      setBags(b.data.bags || []);
    } catch (e) { setError(e?.response?.data?.detail?.code || "LOAD_FAILED"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const confirmPayment = async (kit_order_id) => {
    if (!window.confirm(`Bezahlung für RESCUE-${kit_order_id.slice(0,8).toUpperCase()} bestätigen und 3 Bags freischalten?`)) return;
    setBusy(true); setError("");
    try {
      await api.post(`/rescue/kit/order/${kit_order_id}/confirm-payment`);
      await load();
    } catch (e) { setError(e?.response?.data?.detail?.code || "CONFIRM_FAILED"); }
    finally { setBusy(false); }
  };

  const pending = orders.filter(o => o.status === "PENDING_PAYMENT");
  const paid = orders.filter(o => o.status === "PAID");

  return (
    <div data-testid="admin-rescue-kits-section" className="space-y-4">
      <div className="flex items-center gap-2 border-b border-[#E5E5E5] pb-3">
        <button data-testid="rescue-admin-tab-PENDING" onClick={() => setTab("PENDING")}
          className={`px-3 py-1.5 text-xs font-medium ${tab === "PENDING" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#666] border border-[#E5E5E5]"}`}>
          Zahlung ausstehend ({pending.length})
        </button>
        <button data-testid="rescue-admin-tab-PAID" onClick={() => setTab("PAID")}
          className={`px-3 py-1.5 text-xs font-medium ${tab === "PAID" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#666] border border-[#E5E5E5]"}`}>
          Bezahlte Kits ({paid.length})
        </button>
        <button data-testid="rescue-admin-tab-BAGS" onClick={() => setTab("BAGS")}
          className={`px-3 py-1.5 text-xs font-medium ${tab === "BAGS" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#666] border border-[#E5E5E5]"}`}>
          Aktivierte Bags ({bags.filter(b => b.status === "ACTIVATED").length})
        </button>
      </div>

      {error && <div className="p-2 text-xs text-red-600 bg-red-50">{error}</div>}
      {loading ? (
        <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>
      ) : (
        <>
          {tab === "PENDING" && (
            pending.length === 0 ? (
              <p className="text-xs text-[#999] py-10 text-center">Keine offenen Zahlungen.</p>
            ) : (
              <div className="border border-[#E5E5E5]">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Order</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Seller</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Betrag</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Methode</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Bestellt am</th>
                      <th className="text-right px-3 py-2 font-medium text-[#666]">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map(o => (
                      <tr key={o.kit_order_id} data-testid={`rescue-admin-order-${o.kit_order_id}`} className="border-b border-[#F0F0F0]">
                        <td className="px-3 py-2 font-mono text-[#0A0A0A]">RESCUE-{o.kit_order_id.slice(0,8).toUpperCase()}</td>
                        <td className="px-3 py-2 text-[#333]">{o.seller_display_name || o.seller_email || o.seller_id?.slice(0,8)}</td>
                        <td className="px-3 py-2 text-[#333]">{o.total_amount?.toFixed(2)} €</td>
                        <td className="px-3 py-2 text-[#333]">{o.payment_method}</td>
                        <td className="px-3 py-2 text-[#999]">{o.created_at?.slice(0,10)}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            data-testid={`rescue-admin-confirm-${o.kit_order_id}`}
                            onClick={() => confirmPayment(o.kit_order_id)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1 text-[10px] font-medium bg-[#0A0A0A] text-white rounded disabled:opacity-50">
                            <CheckCircle size={12} /> Zahlung bestätigen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === "PAID" && (
            paid.length === 0 ? (
              <p className="text-xs text-[#999] py-10 text-center">Noch keine bezahlten Kits.</p>
            ) : (
              <div className="border border-[#E5E5E5]">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Order</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Seller</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Betrag</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Bags aktiviert</th>
                      <th className="text-left px-3 py-2 font-medium text-[#666]">Bezahlt am</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paid.map(o => (
                      <tr key={o.kit_order_id} className="border-b border-[#F0F0F0]">
                        <td className="px-3 py-2 font-mono text-[#0A0A0A]">RESCUE-{o.kit_order_id.slice(0,8).toUpperCase()}</td>
                        <td className="px-3 py-2 text-[#333]">{o.seller_display_name || o.seller_email || o.seller_id?.slice(0,8)}</td>
                        <td className="px-3 py-2 text-[#333]">{o.total_amount?.toFixed(2)} €</td>
                        <td className="px-3 py-2 text-[#333]">{o.bags_activated || 0} / {o.bags_issued || 0}</td>
                        <td className="px-3 py-2 text-[#999]">{o.paid_at?.slice(0,10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === "BAGS" && (
            <>
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { v: "", l: "Alle" },
                  { v: "ISSUED", l: "Ausgegeben" },
                  { v: "ACTIVATED", l: "Aktiviert" },
                ].map(s => (
                  <button key={s.v} data-testid={`rescue-admin-bag-status-${s.v || "all"}`}
                    onClick={() => setStatusFilter(s.v)}
                    className={`px-2 py-1 text-[10px] font-medium border ${statusFilter === s.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
                    {s.l}
                  </button>
                ))}
              </div>
              {bags.length === 0 ? (
                <p className="text-xs text-[#999] py-10 text-center">Keine Bags.</p>
              ) : (
                <div className="border border-[#E5E5E5]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Bag-ID</th>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Status</th>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Kategorie</th>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Tragedauer</th>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Seller</th>
                        <th className="text-left px-3 py-2 font-medium text-[#666]">Aktiviert am</th>
                        <th className="text-right px-3 py-2 font-medium text-[#666]">Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bags.map(b => (
                        <tr key={b.bag_id} className="border-b border-[#F0F0F0]">
                          <td className="px-3 py-2 font-mono text-[#0A0A0A]">{b.bag_id.slice(0,8).toUpperCase()}</td>
                          <td className="px-3 py-2">
                            {b.status === "ACTIVATED" ? (
                              <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle size={12} /> Aktiviert</span>
                            ) : b.status === "ISSUED" ? (
                              <span className="inline-flex items-center gap-1 text-[#666]"><Package size={12} /> Ausgegeben</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[#999]"><Clock size={12} /> {b.status}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-[#333]">{b.activation?.category || "—"}</td>
                          <td className="px-3 py-2 text-[#333]">{b.activation?.wear_duration_days ? `${b.activation.wear_duration_days} Tage` : "—"}</td>
                          <td className="px-3 py-2 text-[#666]">{b.seller_id?.slice(0,8)}</td>
                          <td className="px-3 py-2 text-[#999]">{b.activation?.activated_at?.slice(0,10) || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/rescue/bags/${b.bag_id}/label.pdf`}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[#0A0A0A]">
                              <ExternalLink size={12} /> PDF
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
